import {cva, type VariantProps} from 'class-variance-authority'
import type {InputHTMLAttributes} from 'react'
import {forwardRef} from 'react'

const radioWrapperVariants = cva('focus-group flex w-fit items-center gap-8', {
  variants: {
    disabled: {
      true: 'cursor-default',
      false: 'cursor-pointer',
    },
  },
  defaultVariants: {
    disabled: false,
  },
})

const radioInputVariants = cva(
  [
    'ease-out transition-[color,background-color,border-color] duration-150',
    'relative size-16 appearance-none rounded-full border-1 border-border-button-secondary',
    "before:absolute before:top-1/2 before:left-1/2 before:-translate-1/2 before:rounded-full before:bg-surface before:content-['']",
    'before:ease-out before:size-full before:transition-[width,height] before:duration-150 focus:outline-none',
    'checked:before:size-[7px]',
  ],
  {
    variants: {
      disabled: {
        true: 'cursor-default checked:border-button-disabled checked:bg-button-disabled',
        false:
          'cursor-pointer checked:border-button-selected checked:bg-button-selected hover:border-border-button-secondary-hover active:border-border-button-secondary-pressed',
      },
    },
    defaultVariants: {
      disabled: false,
    },
  },
)

const labelVariants = cva('font-light select-none', {
  variants: {
    size: {
      s: 'text-sm leading-sm',
      l: 'text-base leading-base',
    },
    disabled: {
      true: 'text-text-disabled',
      false: 'text-text',
    },
  },
  defaultVariants: {
    size: 's',
    disabled: false,
  },
})

/**
 * The visual radio dot, usable without a form input — e.g. inside a
 * `<NavLink>` for navigation-based toggles. Pass `isActive` to control
 * the checked visual state.
 */
interface RadioIndicatorProps {
  isActive?: boolean
  disabled?: boolean
  label?: string
  size?: VariantProps<typeof labelVariants>['size']
}

export function RadioIndicator({isActive, disabled, label, size}: RadioIndicatorProps) {
  return (
    <>
      <span
        className={radioInputVariants({
          disabled,
          className:
            isActive === true
              ? 'border-button-selected bg-button-selected before:!size-[7px]'
              : undefined,
        })}
      />
      {label && <span className={labelVariants({size, disabled})}>{label}</span>}
    </>
  )
}

interface RadioProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'>,
    VariantProps<typeof labelVariants> {
  label?: string
  disabled?: boolean
}

/**
 * Figma: https://www.figma.com/design/qS60CfMVFGs07l2G3yfhsB/%F0%9F%92%A0-Components?node-id=6032-5657&m=dev
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({className, label, size, disabled, ...props}, ref) => {
    return (
      <label className={radioWrapperVariants({disabled, className})}>
        <input
          {...props}
          type="radio"
          ref={ref}
          disabled={disabled}
          className={radioInputVariants({disabled})}
        />
        {label && <span className={labelVariants({size, disabled})}>{label}</span>}
      </label>
    )
  },
)
Radio.displayName = 'Radio'
